import type { FormBlocks } from "@quillforms/types";
import { FC } from "react";
import { BlockSupportedFeatures } from "@quillforms/blocks";
import { BlockAttributes } from "@quillforms/types/build-types/types/form-blocks";

export interface TemporaryFile {
  id: number;
  path: string;
  quiz_form_answer_id: number;
  created_at: string;
  updated_at: string;
}

interface BlockAnswer {
  created_at: string;
  id: number;
  question_id: number;
  updated_at: string;
  user_id: number;
  value: { answer: string } | string[];
  temporary_files?: TemporaryFile[];
}

export enum InnerBlockType {
  Textarea = "textarea",
  GallbladderDate = "gallbladder_date",
  Error = "error",
}

export interface IQuestionOptions {
  id: string;
  value: string;
  label: string;
  innerBlock?: {
    label: string;
    type: InnerBlockType;
    placeholder?: string;
  };
}

export type ProductConsentGroup = "sermorelin" | "nad";

interface CustomBlockAttributes extends BlockAttributes {
  conditionalBlocks: Record<string, any>;
  question?: string;
  prompt?: string;
  customHTML?: string;
  unit?: string;
  maxLength?: number;
  textarea?: { placeholder?: string };
  options?: IQuestionOptions[];
  note?: string;
  descriptionBlock?: QuestionDescriptionBlock;
  title?: string;
  subtitle?: string;
  message?: string;
  type?: string;
  maxFiles?: number;
  maxFileSizeMB?: number;
  answerType?: FileAnswerType;
  progressCategory?: string;
  disableFuture?: boolean;
  redirectUrl?: string;
  appendVoucherId?: boolean;
  /** Skips only the minimum-BMI eligibility check; measurement validation remains enabled. */
  skipBmiValidation?: boolean;
  productConsentGroup?: ProductConsentGroup;
  /** Asked of female patients only; the backend drops it for known males */
  isFemale?: boolean;
  /** On the last question, reload the page instead of finishing the quiz */
  sermorelinAgentRerfresh?: boolean;
  range?: {
    min?: number;
    max?: number;
  };
  /** Remaining Vouched SSN-verification attempts for this order (null = unknown). */
  vouched_attempts_left?: number | null;
}

export type FormBlock = {
  id: string;
  name: string;
  attributes?: BlockAttributes;
  innerBlocks?: FormBlock[];
  content_type?:
    | "medication_waiver_consent"
    | "contacts"
    | "bmi"
    | "gender"
    | "weight_goal"
    | "diseases"
    | "current_medications"
    | "allergies"
    | "before_verification"
    | "sumsub_verification"
    | "current_weight_loss_medication"
    | "lab_result"
    | "body_photo"
    | "pdf_image"
    | null;
};

export type QuestionDescriptionBlock = {
  title?: string;
  content?: string[];
  customHTML?: string;
}[];

export interface CustomFormBlock extends FormBlock {
  is_answered: boolean;
  order: number;
  answers: BlockAnswer[];
  attributes: CustomBlockAttributes;
}

interface AnswerBase {
  blockName: string;
  isAnswered: boolean;
  isCorrect?: boolean;
  isCorrectIncorrectScreenDisplayed: boolean;
  isLocked: boolean;
  isPending: boolean;
  isValid: boolean;
  pendingMsg?: string;
  validationErr?: string;
}

export type FileAnswerType =
  | "id"
  | "photo"
  | "video"
  | "pdf_image"
  | "phi_report"
  | "body_photo"
  | "current_weight_loss_medication"
  | "lab_result"
  | null;

interface FileAnswer extends AnswerBase {
  blockName: "custom-attachment";
  value: { type: FileAnswerType; files: File[] };
}

interface TextAnswer extends AnswerBase {
  blockName: "error-screen";
  value: string;
}

interface ObjectAnswer extends AnswerBase {
  blockName:
    | "custom-input-field"
    | "select-sex"
    | "custom-long-text"
    | "contacts-fields-cpa"
    | "contacts-fields"
    | "checkout-address"
    | "shipping-fields-checkout"
    | "contacts-fields-checkout";
  value: { answer: string };
}

interface SingleChoiceAnswer extends AnswerBase {
  blockName: "custom-single-choice";
  value: { answer: string; inputValue?: string };
}

interface ArrayAnswer extends AnswerBase {
  blockName: "custom-multiple-choice";
  value: string[];
}

interface HeightWeightInputAnswer extends AnswerBase {
  blockName: "height-weight-input-fields";
  value: { weight: string; feet: string; inches: string };
}

export type AnswerValue =
  | FileAnswer["value"]
  | TextAnswer["value"]
  | SingleChoiceAnswer["value"]
  | ArrayAnswer["value"]
  | ObjectAnswer["value"]
  | HeightWeightInputAnswer["value"];

export type Answer =
  | FileAnswer
  | TextAnswer
  | SingleChoiceAnswer
  | ArrayAnswer
  | ObjectAnswer
  | HeightWeightInputAnswer;

export interface BeforeGoingNextArgs {
  setIsFieldValid: (id: string, flag: boolean) => void;
  setFieldValidationErr: (id: string, err: string) => void;
  setIsPending: (flag: boolean) => void;
  setIsCurrentBlockSafeToSwipe: (flag: boolean) => void;
  goToBlock: (id: string) => void;
  goNext: () => void;
  currentBlockId: string;
  answers: Record<string, unknown>;
}

export interface QuizFormProps {
  blocks: FormBlocks;
  shouldRedirect: boolean;
  beforeGoingNext?: (args: BeforeGoingNextArgs) => void;
  disableButton: boolean;
}

export interface BlockConfiguration {
  name: string;
  supports: BlockSupportedFeatures;
  attributes: Record<string, any>;
  display: FC<any>;
}
